import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { toCssVars, type ThemeMode } from "@isale/design-tokens";

export type ThemePref = "light" | "dark" | "system";

interface ThemeContextValue {
  mode: ThemePref;
  resolved: ThemeMode;
  setMode: (m: ThemePref) => void;
}

const STORAGE_KEY = "isale.theme_pref";

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Web theme provider — sets CSS variables on :root + toggles the
 * Tailwind `dark` class so utility classes flip with mode. Persists to
 * localStorage. `system` mode subscribes to `prefers-color-scheme`
 * changes via matchMedia.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemePref>(() => {
    if (typeof window === "undefined") return "system";
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" || v === "system" ? v : "system";
  });
  const [resolved, setResolved] = useState<ThemeMode>("light");

  // Resolve + react to OS changes.
  useEffect(() => {
    function pick(): ThemeMode {
      if (mode === "system") {
        return window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light";
      }
      return mode;
    }
    setResolved(pick());
    if (mode !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    function listener(e: MediaQueryListEvent) {
      setResolved(e.matches ? "dark" : "light");
    }
    mql.addEventListener("change", listener);
    return () => mql.removeEventListener("change", listener);
  }, [mode]);

  // Apply CSS vars + dark class on every resolve change.
  useEffect(() => {
    const root = document.documentElement;
    const vars = toCssVars(resolved);
    for (const [name, value] of Object.entries(vars)) {
      root.style.setProperty(name, value);
    }
    if (resolved === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    // Apply native color-scheme so form controls + scrollbars match.
    root.style.colorScheme = resolved;
  }, [resolved]);

  const setMode = useCallback((m: ThemePref) => {
    setModeState(m);
    try {
      window.localStorage.setItem(STORAGE_KEY, m);
    } catch {
      // best-effort
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, resolved, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme() must be used inside <ThemeProvider />.");
  }
  return ctx;
}

// ADR-011 P4 — the `useThemeColors()` / BRIDGED_COLORS transition bridge was
// removed here once all consumers migrated to semantic token classes
// (text-fg, bg-surface, …) + CSS-var inline styles. Theme-aware colour now
// flows entirely through the `.dark` root class + `--color-*` variables.
// Use `useTheme()` for the resolved mode; never reintroduce a colour-object hook.
